import { ProductSuggestionStatus } from '@/modules/flokee/interfaces/productSuggestion.interface';
import { flokService } from '@/modules/flokee/services/flok.service';
import {
  EcommerceSocketEvents,
  type SuggestionProgressEventPayload,
} from '@/modules/flokee/sockets/ecommerce.socket.interface';
import { useEcommerceStore } from '@/modules/flokee/stores/ecommerce.store';
import { queryOptions, useQuery } from '@tanstack/vue-query';
import type { MaybeRefOrGetter } from 'vue';
import { useCurrentEcommerce } from '@/modules/flokee/composables/useCurrentEcommerce';

const PRODUCT_SUGGESTIONS_POLL_INTERVAL_MS = 4000;
const PRODUCT_SUGGESTIONS_TIMEOUT_MS = 90000;

interface UseProductSuggestionsOptions {
  enableRealtime?: boolean;
}

export const getProductSuggestionsQueryOptions = (ecommerce_id: number, product_id: number) =>
  queryOptions({
    queryKey: ['product-suggestion', ecommerce_id, product_id],
    queryFn: () => flokService.getProductSuggestions(ecommerce_id, product_id),
    select: (response) => response?.data.data,
    notifyOnChangeProps: 'all',
    gcTime: Infinity,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

export const useProductSuggestions = (
  product_id?: MaybeRefOrGetter<number | undefined>,
  options: UseProductSuggestionsOptions = {},
) => {
  const { currentEcommerce } = useCurrentEcommerce();
  const ecommerceStore = useEcommerceStore();

  const productId = computed(() => toValue(product_id));
  const ecommerceId = computed(() => currentEcommerce.value?.id);
  const enableRealtime = options.enableRealtime ?? true;

  const suggestionsQueryIsEnabled = computed(() => !!productId.value && !!ecommerceId.value);

  const currentStep = ref<SuggestionProgressEventPayload['step']>('started');
  const loadingProgress = ref(0);
  const hasTimedOut = ref(false);

  let fallbackPollInterval: ReturnType<typeof setInterval> | undefined;
  let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
  let loadingProgressInterval: ReturnType<typeof setInterval> | undefined;

  const resetProgressState = () => {
    currentStep.value = 'started';
    loadingProgress.value = 0;
    hasTimedOut.value = false;
  };

  const applyProgressFloor = (step: SuggestionProgressEventPayload['step']) => {
    switch (step) {
      case 'started':
        if (loadingProgress.value < 20) loadingProgress.value = 20;
        break;
      case 'screenshot_taken':
        if (loadingProgress.value < 45) loadingProgress.value = 45;
        break;
      case 'html_scrapped':
        if (loadingProgress.value < 75) loadingProgress.value = 75;
        break;
      case 'done':
        loadingProgress.value = 100;
        break;
      case 'error':
        break;
    }
  };

  const clearFallbackPoll = () => {
    if (!fallbackPollInterval) return;
    clearInterval(fallbackPollInterval);
    fallbackPollInterval = undefined;
  };

  const clearTimeoutTimer = () => {
    if (!timeoutTimer) return;
    clearTimeout(timeoutTimer);
    timeoutTimer = undefined;
  };

  const clearLoadingProgressInterval = () => {
    if (!loadingProgressInterval) return;
    clearInterval(loadingProgressInterval);
    loadingProgressInterval = undefined;
  };

  const {
    data: productSuggestion,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['product-suggestion', ecommerceId, productId],
    queryFn: ({ queryKey: [, id, product] }) => {
      if (!id || !product) {
        throw new Error('Product suggestions query called without ecommerce or product id.');
      }

      return flokService.getProductSuggestions(id as number, product as number);
    },
    select: (response) => response?.data.data,
    notifyOnChangeProps: 'all',
    gcTime: Infinity,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: suggestionsQueryIsEnabled,
  });

  const status = computed(() => productSuggestion.value?.status);
  const isDraft = computed(() => status.value === ProductSuggestionStatus.Draft);
  const isProcessing = computed(() => status.value === ProductSuggestionStatus.Processing);
  const isDone = computed(() => status.value === ProductSuggestionStatus.Done);
  const isError = computed(() => status.value === ProductSuggestionStatus.Error);
  const isWaitingForGeneration = computed(() => isDraft.value || isProcessing.value);

  const refetchSuggestions = async () => {
    if (!suggestionsQueryIsEnabled.value) return;

    await refetch();
  };

  watch(
    [productId, ecommerceId],
    () => {
      clearFallbackPoll();
      clearTimeoutTimer();
      clearLoadingProgressInterval();
      resetProgressState();
    },
    { immediate: true },
  );

  watch(
    isWaitingForGeneration,
    (waiting) => {
      clearLoadingProgressInterval();

      if (!waiting) {
        if (isDone.value) loadingProgress.value = 100;
        return;
      }

      applyProgressFloor(currentStep.value);

      loadingProgressInterval = setInterval(() => {
        if (loadingProgress.value >= 99) {
          clearLoadingProgressInterval();
          return;
        }

        switch (currentStep.value) {
          case 'started':
            if (loadingProgress.value < 40) loadingProgress.value++;
            break;
          case 'screenshot_taken':
            if (loadingProgress.value < 70) loadingProgress.value++;
            break;
          case 'html_scrapped':
            if (loadingProgress.value < 95) loadingProgress.value++;
            break;
          case 'done':
            loadingProgress.value = 100;
            break;
          case 'error':
            break;
        }
      }, 1000);
    },
    { immediate: true },
  );

  watch(
    isWaitingForGeneration,
    (waiting) => {
      clearFallbackPoll();
      if (!waiting) return;

      fallbackPollInterval = setInterval(() => {
        void refetchSuggestions();
      }, PRODUCT_SUGGESTIONS_POLL_INTERVAL_MS);
    },
    { immediate: true },
  );

  watch(
    isWaitingForGeneration,
    (waiting) => {
      clearTimeoutTimer();
      hasTimedOut.value = false;

      if (!waiting) return;

      timeoutTimer = setTimeout(() => {
        hasTimedOut.value = true;
      }, PRODUCT_SUGGESTIONS_TIMEOUT_MS);
    },
    { immediate: true },
  );

  watch(
    [() => ecommerceStore.ecommerceSocket, suggestionsQueryIsEnabled, isDone],
    ([socket, queryEnabled, done], _, onCleanup) => {
      if (!enableRealtime || !socket || !queryEnabled || done) return;

      const onSuggestionProgress = async (event: SuggestionProgressEventPayload) => {
        if (event.ecommerce_id !== ecommerceId.value || event.product_id !== productId.value) {
          return;
        }

        currentStep.value = event.step;
        applyProgressFloor(event.step);

        if (event.step === 'done' || event.step === 'error') {
          await refetchSuggestions();
        }
      };

      socket.onSuggestionProgress(onSuggestionProgress);

      onCleanup(() => {
        socket.unbind(EcommerceSocketEvents.SuggestionProgress);
      });
    },
    { immediate: true },
  );

  watch(status, (newStatus) => {
    if (
      newStatus === ProductSuggestionStatus.Draft ||
      newStatus === ProductSuggestionStatus.Processing
    ) {
      if (currentStep.value === 'error' || currentStep.value === 'done') {
        currentStep.value = 'started';
      }
      applyProgressFloor(currentStep.value);
    }

    if (newStatus === ProductSuggestionStatus.Done) {
      currentStep.value = 'done';
      loadingProgress.value = 100;
    }

    if (newStatus === ProductSuggestionStatus.Error) {
      currentStep.value = 'error';
    }
  });

  onBeforeUnmount(() => {
    clearFallbackPoll();
    clearTimeoutTimer();
    clearLoadingProgressInterval();
    ecommerceStore.ecommerceSocket?.unbind(EcommerceSocketEvents.SuggestionProgress);
  });

  const suggestionsByCategory = computed(() => {
    return (
      productSuggestion.value?.suggestions.reduce(
        (acc, suggestion) => {
          if (!acc[suggestion.category]) {
            acc[suggestion.category] = [];
          }
          acc[suggestion.category].push({
            suggestion: suggestion.suggestion,
            title: suggestion.title,
          });
          return acc;
        },
        {} as Record<string, { suggestion: string; title: string }[]>,
      ) ?? {}
    );
  });

  return {
    suggestions: computed(() => productSuggestion.value?.suggestions ?? []),
    suggestionsByCategory,
    status,
    image: computed(() => productSuggestion.value?.image),
    product: computed(() => productSuggestion.value?.product),
    currentStep,
    loadingProgress,
    hasTimedOut,
    isReady: isDone,
    isDone,
    isProcessing,
    isDraft,
    isError,
    isLoading,
    refetchSuggestions,
  };
};
